import { Box, useTheme } from "@mui/system";
import {Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";

import ClientMeetings from "./ClientMeetings";
import {useLocation, useNavigate, useParams } from "react-router-dom";
import ClientActionItem from "./ClientActionItem";
import { useEffect, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { capitalizeFirstLetter, generateLast12MonthsColumns } from "../../utils/utils";
import ClientPendingActionItem from "./ClientPendingActionItem";
import dayjs from "dayjs";
import { Tooltip } from "@mui/material";
import useGlobalContext from "../../context/useGlobalContext";
import ClientService from "../../services/clients/ClientService";
import SwitchComponent from "./SwitchComponent";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { InteractionCounts } from "../../utils/types";
import "../../styles/common.css";


type MonthlyInsight = {
  monthYear: string;
  finalScore: number;
  npsCategory: 'Promoter' | 'Detractor' | 'Neutral';
};


const ClientMeetingActionView = () => {
  const location = useLocation();
  const selectedNps = location.state?.selectedNps || null;
  const startEndMeetingTimes = location.state?.startEndMeetingTime || null;
  const newUserTeamData = location.state?.newUserTeamData;
  const loggedInEmail = location.state?.loggedInEmail || null;
  const selectedUserEmail = location.state?.selectedUserEmail || null;
  const { startEndMeetingTime } = useGlobalContext()
  const theme = useTheme();
  const navigate = useNavigate();
  const { client_domain } = useParams<{ client_domain: string }>();
  const nameFromEmail = client_domain ? client_domain.split(".")[0] || "" : "";
  const formattedName = capitalizeFirstLetter(nameFromEmail);
  const rated = location.state?.rated ?? true;
  const tableFilters = location.state?.tableFilters;
  const [tabValue, setTabValue] = useState(0);  
  const [showPendingActionItem, setShowPendingActionItem] = useState(false);  
  const [monthlyData, setMonthlyData] = useState<MonthlyInsight[]>([]);
  const [startTime, setStartTime] = useState<string>(dayjs().subtract(1, "year").startOf("day").format("YYYY-MM-DDTHH:mm:ssZ"));
  const [endTime, setEndTime] = useState<string>(dayjs().format("YYYY-MM-DDTHH:mm:ssZ"));
  const [rows, setRows] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [isRated , setIsRated] = useState(rated);
  const [userToggled, setUserToggled] = useState(false);

    const handleViewAllClick = () => {
      const fullStartTime = dayjs().subtract(1, "years").startOf("year").format("YYYY-MM-DDTHH:mm:ssZ");
      const fullEndTime = dayjs().format("YYYY-MM-DDTHH:mm:ssZ");
      setStartTime(fullStartTime);  // Update start time for full data
      setEndTime(fullEndTime);      // Update end time for full data
      setSelectedMonth(null);       // Reset selected month when View All is clicked

    };
  const hideMonthlyViewInsights = false;
  const [counts, setCounts] = useState<InteractionCounts>({ rated: 0, total: 0 });
  const generateRows = (monthlyData: MonthlyInsight[]) => {
    const row: Record<string, any> = { id: "monthly-insight-row" };
  
    // Generate circles for all columns (last 12 months)
    generateLast12MonthsColumns().forEach(({ field }) => {
      const monthYear = dayjs(field, "MMM YYYY").format("YYYY-MM"); // Format the field to month-year
  
      // Find the matching data for the current month
      const dataForMonth = monthlyData.find((item) => item.monthYear === monthYear);
  
      if (dataForMonth) {
        const isSelected = selectedMonth === dataForMonth.monthYear;
  
        // Determine the circle color
        const color =
          dataForMonth.npsCategory === "Promoter"
            ? "#32BD00" // Green for promoters
            : dataForMonth.npsCategory === "Neutral"
            ? "#FFC700" // Orange for neutral
            : dataForMonth.npsCategory === "Detractor"
            ? "#FF2E00" // Yellow for detractors
            : "gray"; // Default to gray if no category is found
  
        // Render the circle and highlight the selected one
        row[field] = (
          <Tooltip
            title={
              <>
                {/* <div>Score: {dataForMonth.final_score.toFixed(2)}</div> */}
                <div>Category: {capitalizeFirstLetter(dataForMonth.npsCategory)}</div>
              </>
            }
            placement="bottom"
            arrow
          >
            <Box
              onClick={() => {
                const startOfMonth = dayjs(dataForMonth.monthYear)
                  .startOf("month")
                  .format("YYYY-MM-DDTHH:mm:ssZ");
                const endOfMonth = dayjs(dataForMonth.monthYear)
                  .endOf("month")
                  .format("YYYY-MM-DDTHH:mm:ssZ");
                setStartTime(startOfMonth);
                setEndTime(endOfMonth); // Set end of the month
                setSelectedMonth(dataForMonth.monthYear); // Update the selected month
              }}
              sx={{
                backgroundColor: color,
                width: 25,
                height: 25,
                borderRadius: "50%",
                cursor: "pointer",
                margin: "auto",
                border: isSelected ? `2px solid ${theme.palette.primary.main}` : "none",
                transform: isSelected ? "scale(1.2)" : "scale(1)",
                transition: "transform 0.2s ease, border 0.2s ease",
              }}
            />
          </Tooltip>
        );
      } else {
        // If no data for that month, render an empty placeholder (circle is absent)
        row[field] = null;
      }
    });
  
    return [row]; // Return the single row as an array
  };
  
  const {
    data: monthlyInsightData
  } = ClientService.useGetMonthlyInsights(
    {
      startTime: startTime,
      endTime: endTime,
      domain: client_domain as string,
    },
    false
  );
  
  const handleToggleChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsRated(checked); 
    setUserToggled(true);
  };
  
  useEffect(() => {
    if (monthlyInsightData?.data) {
      setMonthlyData(monthlyInsightData?.data); 
      const generatedRows = generateRows(monthlyInsightData?.data);
      setRows(generatedRows); 
      console.log("Rows generated and set from API response:", generatedRows);

    }
  }, [monthlyInsightData]);
  
  useEffect(() => {
    if (monthlyData?.length > 0) {
      setRows(generateRows(monthlyData)); 
      console.log("Rows passed:", rows);
    }
  }, [monthlyData]);



  useEffect(() => {
    
    const showPendingAction = localStorage.getItem("showPendingAction");
    if (showPendingAction === "true") {
      setTabValue(1);  
      setShowPendingActionItem(true);  
      localStorage.removeItem("showPendingAction");  
    }
  }, []);

  useEffect(() => {
    if (!userToggled && counts.rated === 0 && counts.total > 0) {
      setIsRated(false);
    }
  }, [counts]);
  
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBackClick = () => {
    if (location.state?.ParentPageIdentifier === "Reporting") {
      navigate("/reporting");
    } else {
      navigate("/client", {
        state: {
          selectedNps,
          startEndMeetingTimes,
          newUserTeamData,
          rated,
          tableFilters,
        },
      });
    }
  };
  return (
    <Box className="meetingsContent w-full">

      {hideMonthlyViewInsights && (
      <Box

        sx={{
          height: "100%",
          padding: "15px",
          border: `2px solid ${theme.palette.secondary.main}`,
          borderRadius: "22px",
          background: "#FFFFFF",
          marginBottom: "20px"

        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{marginBottom: "10px"}}>Monthly View Insights of '{formattedName}'</Typography>
                { selectedMonth && <Button
             
  // variant="contained"
  color="primary"
  onClick={handleViewAllClick}
  sx={{ marginBottom: "16px",background: 'none', color: 'black', textTransform: "none", fontSize: "16px"
  }}
>
  View All
</Button>}
        </Box>
               

        <TableContainer 
           sx={{
            height: "100%",
            padding: "15px",
            border: `2px solid ${theme.palette.secondary.main}`,
            borderRadius: "22px",
            background: "#FFFFFF",
            marginBottom: "4px"
          }}>
          <Table>
            <TableHead>
              <TableRow>
                {generateLast12MonthsColumns().map(({ field }, index) => (
                  <TableCell key={index} align="center" sx={{ fontWeight: "400", fontSize: "18px" }}>
                    {field}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 && (
                <TableRow
                sx={{borderBottom: `0px solid ${theme.palette.secondary.main}`}}>
                  {generateLast12MonthsColumns().map(({ field }, index) => (
                    <TableCell key={index} align="center" sx={{borderBottom: "none"}}>
                      {rows[0][field]}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box> )}
      
      <Box
        sx={{
          height: "100%",
          padding: "15px",
          border: `2px solid ${theme.palette.secondary.main}`,
          borderRadius: "22px",
          background: "#FFFFFF",
        }}
      >
        <Box className="flex flex-row justify-start items-center gap-2">
        <Box className="back-icon-button" onClick={handleBackClick}>
            <Tooltip title="Go back" placement="top" arrow>
            <ArrowBackIcon sx={{ fontSize: "28px" }} />
            </Tooltip>
        </Box>
          <Typography variant="h6">
            {showPendingActionItem
              ? `Pending Tasks for '${formattedName}'`
              : (tabValue === 0
                ? `Interactions with '${formattedName}'`
                : `Tasks for '${formattedName}'`)
            }
          </Typography>
        </Box>

        {!showPendingActionItem && (
          <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
          }}
          >
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Interactions" />
            <Tab label="Action Items" />
          </Tabs>
          <Box
            sx={{
              visibility: tabValue === 0 ? 'visible' : 'hidden',
            }}
          >
          <Box sx={{ mb:-1.5, textDecoration: 'none' }}>
            <SwitchComponent
              rated={isRated}
              firstText="All"
              secondText="Rated"
              onToggleChange={handleToggleChange}
              ratedCount={counts.rated}
              totalCount={counts.total}
              hideUnderline={true}
            />
          </Box>
          </Box>
        </Box>
        
        )}

        <Box className="flex flex-col justify-start" sx={{ marginTop: "15px" }}>
          {showPendingActionItem ? (
            <ClientPendingActionItem clientDomain={client_domain as string}
            selectedUserEmail={selectedUserEmail}
            loggedInEmail={loggedInEmail}  />
          ) : tabValue === 0 ? (
            <ClientMeetings clientDomain={client_domain as string}  startTime={startEndMeetingTime.start_time} 
            endTime={startEndMeetingTime.end_time}
            selectedUserEmail={selectedUserEmail}
            newUserTeamData={newUserTeamData}
            isRated={isRated}
            setCounts={setCounts}
            parent_page_identifier="Client_Page_interactions"
            />
          ) : (
            <ClientActionItem clientDomain={client_domain as string} 
            selectedUserEmail={selectedUserEmail}
            loggedInEmail={loggedInEmail}
            newUserTeamData={newUserTeamData}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ClientMeetingActionView;